import { observer } from "mobx-react-lite"
import { useState } from "react"
import { useInterval } from "usehooks-ts"

import { DOCS_LINK } from "@/components/footer"
import {
  LANDING_STAT_LOADING,
  type LandingActivityItem,
  type LandingActivityRow,
  type LandingCapability,
  type LandingEpochCountdown,
  type LandingHeroAccount,
  type LandingMetric,
  type LandingOpportunity,
  type LandingOpportunityRail,
  type LandingPersonaRoute,
  type LandingTestimonial,
  type LandingTrustSignal,
} from "@/domains/landing-page/interfaces"
import type { HeroBlockStat } from "@/domains/landing-page/types"
import type { ITopIncentive } from "@/domains/gauge/interfaces"
import { useScaleApplication } from "@/domains/application/use-scale-application"
import { dollars } from "@/domains/library"
import { computeEpochCountdownSnapshot } from "@/domains/landing-page/utils/epoch-countdown"
import { getDisplayTypeShortName, isClFarmDisplayType } from "@/lib/show-display-type"

import { ProfessionalLandingPageView } from "./professional-landing-page-view"

export const ProfessionalLandingPageComposite = observer(function ProfessionalLandingPageComposite() {
  const scaleApp = useScaleApplication()
  const chainSlug = scaleApp.getChainSlug()
  const blockStats = scaleApp.HeroBlockStats.blockStats
  const statById = new Map(blockStats.map((stat) => [stat.id, stat]))
  const topIncentives = scaleApp.Gauge.Repository.getTopIncentivesByGaugeValue(8)
  const [epochCountdown, setEpochCountdown] = useState(() => createEpochCountdown(chainSlug, statById))

  useInterval(() => {
    setEpochCountdown(createEpochCountdown(chainSlug, statById))
  }, 1000)

  const heroAccount = createHeroAccount(scaleApp)
  const activityEvents = createActivityItems(scaleApp.LiveActivity.rows)

  return (
    <ProfessionalLandingPageView
      brutalistHero={BRUTALIST_HERO}
      heroAccount={heroAccount}
      activityEvents={activityEvents}
      heroCtas={[
        { label: "Launch App", href: "/swap", tone: "primary" },
        { label: "Explore Pools", href: "/liquidity", tone: "secondary" },
      ]}
      personaRoutes={PERSONA_ROUTES}
      epochCountdown={epochCountdown}
      metrics={createMetrics(statById)}
      trustSignals={TRUST_SIGNALS}
      opportunityRails={createOpportunityRails(topIncentives)}
      capabilities={CAPABILITIES}
      testimonials={TESTIMONIALS}
    />
  )
})

const truncateAddress = (address: string): string =>
  address.length > 10 ? `${address.slice(0, 6)}…${address.slice(-4)}` : address

/**
 * Builds the connected-wallet hero snapshot from real on-chain state. Returns
 * undefined for logged-out visitors so the hero falls back to its default copy.
 */
const createHeroAccount = (scaleApp: ReturnType<typeof useScaleApplication>): LandingHeroAccount | undefined => {
  const wallet = scaleApp.Wallet.Connection
  if (!wallet.isWalletConnected || wallet.connectedAddress == null) {
    return undefined
  }

  const lockRepository = scaleApp.Lock.Repository
  const hasLocks = lockRepository.totalLocks > 0
  const claimableValueUsd = scaleApp.LockRewards.Repository.totalRewardsValueUSD
  const hasClaimable = claimableValueUsd > 0.005

  // `lockValue` is the veSCALE voting power (not USD); `totalLocks` is the veNFT
  // count. `totalLockPower` is a per-lock ratio sum and is intentionally not
  // surfaced here.
  return {
    addressLabel: truncateAddress(wallet.connectedAddress.toString()),
    hasLocks,
    veScalePowerLabel: Math.round(lockRepository.totalLockValue).toLocaleString("en-US"),
    veNftCountLabel: lockRepository.totalLocks.toLocaleString("en-US"),
    claimableValueLabel: dollars(claimableValueUsd, 2),
    hasClaimable,
    dashboardHref: "/dashboard",
    ctaLabel: "Open Dashboard",
  }
}

const createActivityItems = (rows: readonly LandingActivityRow[]): LandingActivityItem[] => {
  return rows.map((row) => ({
    id: row.id,
    kindLabel: "Swap",
    valueLabel: dollars(row.valueUsd, 2),
    accountLabel: row.account === "" ? "" : truncateAddress(row.account),
    txUrl: row.txUrl,
    timestampSec: row.timestampSec,
  }))
}

const createEpochCountdown = (
  chainSlug: string,
  statById: Map<string, HeroBlockStat>,
): LandingEpochCountdown => {
  const snapshot = computeEpochCountdownSnapshot(chainSlug)

  return {
    ...snapshot,
    epochIncentivesValue: getStatValue(statById, "epoch-incentives"),
    bribeRoiValue: getStatValue(statById, "bribe-roi"),
    voteHref: "/vote",
  }
}

const createMetrics = (statById: Map<string, HeroBlockStat>): LandingMetric[] => {
  return [
    {
      label: "Total TVL",
      value: getStatValue(statById, "tvl"),
      detail: "Across pools and veSCALE locks",
      href: "/treasury",
    },
    {
      label: "Epoch Incentives",
      value: getStatValue(statById, "epoch-incentives"),
      detail: "Available this epoch",
      href: "/vote",
    },
    {
      label: "Incentives Per Vote",
      value: getStatValue(statById, "incentives-per-vote"),
      detail: "Current voting market",
      href: "/vote",
    },
    {
      label: "Emissions Rate",
      value: getStatValue(statById, "emissions-rate"),
      detail: "SCALE per day",
      href: "/liquidity",
    },
  ]
}

const getStatValue = (statById: Map<string, HeroBlockStat>, id: string): string => {
  const value = statById.get(id)?.value ?? LANDING_STAT_LOADING
  return value.replace("🌟", "").replace("🌱", "")
}

const BRUTALIST_HERO = {
  headlineLines: ["Base's", "Native", "Decentralized", "Exchange"],
  subcopyLines: ["Built on Base.", "Owned by the community."],
  ctaLabel: "Explore Markets",
  ctaHref: "/liquidity",
  bustImageSrc: "/images/landing/hero-bust-cutout.png",
} as const

const PERSONA_ROUTES: LandingPersonaRoute[] = [
  {
    label: "Swap",
    description: "Trade with deep Base liquidity",
    href: "/swap",
    icon: "swap",
  },
  {
    label: "Farm LP",
    description: "Deposit into top-yield pools",
    href: "/liquidity",
    icon: "liquidity",
  },
  {
    label: "Lock & Vote",
    description: "Earn weekly veSCALE rewards",
    href: "/lock",
    icon: "lock",
  },
  {
    label: "Explore Pools",
    description: "Browse live APRs, no wallet needed",
    href: "/liquidity",
    icon: "explore",
  },
]

const TRUST_SIGNALS: LandingTrustSignal[] = [
  { label: "100% on-chain" },
  { label: "Built on Base" },
  { label: "Public treasury", href: "/treasury" },
  { label: "Docs", href: DOCS_LINK },
]

const createOpportunityRails = (topIncentives: ITopIncentive[]): LandingOpportunityRail[] => {
  if (topIncentives.length === 0) {
    return [
      {
        title: "Explore live pools",
        subtitle: "Getting started",
        href: "/liquidity",
        opportunities: FALLBACK_OPPORTUNITIES,
      },
    ]
  }

  const byRewards = createOpportunities(topIncentives.slice(0, 4))
  const byApr = createOpportunities(
    [...topIncentives]
      .sort((left, right) => Math.max(right.apr, right.gaugeApr) - Math.max(left.apr, left.gaugeApr))
      .slice(0, 4),
  )

  return [
    {
      title: "Highest rewards this epoch",
      subtitle: "Trending",
      href: "/vote",
      opportunities: byRewards,
    },
    {
      title: "Best APR right now",
      subtitle: "Yield",
      href: "/liquidity",
      opportunities: byApr,
    },
  ]
}

const createOpportunities = (topIncentives: ITopIncentive[]): LandingOpportunity[] => {
  if (topIncentives.length === 0) {
    return FALLBACK_OPPORTUNITIES
  }

  return topIncentives.map((pool) => {
    const apr = Math.max(pool.apr, pool.gaugeApr)
    const href = isClFarmDisplayType(pool.displayType)
      ? `/liquidity/cl-farms/${pool.address.toString()}/mint`
      : `/liquidity/${pool.address.toString()}/add`

    return {
      title: pool.displayName,
      subtitle: getDisplayTypeShortName(pool.displayType),
      primaryValue: dollars(pool.gaugeValueUSD, 2),
      secondaryValue: apr > 0 ? `${apr.toFixed(2)}%` : "Review",
      href,
      tokenLogos: pool.tokens.slice(0, 2).map((token) => ({
        symbol: token.symbol,
        imageUrl: token.imageURL,
        chainSlug: token.chainSlug,
      })),
    }
  })
}

const FALLBACK_OPPORTUNITIES: LandingOpportunity[] = [
  {
    title: "Classic Liquidity",
    subtitle: "Pools",
    primaryValue: "Explore",
    secondaryValue: "Live",
    href: "/liquidity",
  },
  {
    title: "Concentrated Liquidity",
    subtitle: "CL Farms",
    primaryValue: "Manage",
    secondaryValue: "Live",
    href: "/liquidity/cl-farms",
  },
  {
    title: "Voting Rewards",
    subtitle: "veSCALE",
    primaryValue: "Vote",
    secondaryValue: "Weekly",
    href: "/vote",
  },
  {
    title: "Treasury",
    subtitle: "Transparency",
    primaryValue: "View",
    secondaryValue: "Public",
    href: "/treasury",
  },
]

const CAPABILITIES: LandingCapability[] = [
  {
    title: "Swap & Provide Liquidity",
    stat: "Swap + LP",
    icon: "swap",
    description: "Trade and add liquidity, then point incentives at the pools that matter on Base.",
  },
  {
    title: "Lock veSCALE",
    stat: "Locks",
    icon: "lock",
    description: "Lock SCALE for voting power, weekly rewards, and a real say in where emissions go.",
  },
  {
    title: "Track Everything",
    stat: "Analytics",
    icon: "chart",
    description: "Volume, treasury, epochs, and live incentives in one dashboard.",
  },
]

const TESTIMONIALS: LandingTestimonial[] = [
  {
    quote: [
      "I've been with Scale since launch and love the team's energy and fresh direction. It's growing beyond a DEX, mixing on-chain yield with off-chain revenue.",
      "The AI-driven businesses and treasury-funded buybacks have me excited. I'm buying more Scale.",
    ],
    authorName: "James",
    role: "veSCALE voter",
    initials: "J",
  },
  {
    quote: [
      "Scale.Farm has been an aligned and supportive partner to the Fame Lady Society and $FAME on Base.",
      "Over 18 months their team stayed engaged and collaborative, identifying liquidity opportunities and executing incentive campaigns. We're optimistic about what we'll build next.",
    ],
    authorName: "Flick D",
    role: "Fame Lady Society",
    initials: "FD",
  },
  {
    quote: [
      "After 9 years in crypto, I can spot real builders. Scale on Base stands out.",
      "The dev moves with and ahead of trends instead of chasing them. Near-daily updates and a clear focus on real external revenue inspire confidence.",
    ],
    authorName: "Yardie",
    role: "veSCALE voter",
    initials: "YD",
  },
  {
    quote: [
      "User experience on this DEX is top tier. Swaps are instant, slippage is minimal, and the 42-day farming lock keeps liquidity committed.",
      "I'm impressed by the direction with AI and how openly they communicate. It doesn't feel like hype. They're shipping agentic systems for real.",
    ],
    authorName: "0xMaximalist",
    role: "Trader",
    initials: "Max",
  },
]
